import type { LayerRole } from './layer-naming'
import type { LayerUrls, PerTierEntry, TierRule } from './types'

export type CssBlend = 'normal' | 'multiply' | 'soft-light'

export const ROLE_BLEND: Readonly<Record<LayerRole, CssBlend>> = {
  skin: 'normal',
  whites: 'normal',
  shadow: 'soft-light',
  highlight: 'soft-light',
  outline: 'multiply',
  pupilColour: 'normal',
  pupilDetail: 'normal',
  pupilReflection: 'normal',
  hairBack: 'normal',
  hairBackShadow: 'soft-light',
  hairBackHighlight: 'soft-light',
  hairBackOutline: 'multiply',
}

const BACK_ROLE_ORDER: readonly LayerRole[] = [
  'hairBack',
  'hairBackShadow',
  'hairBackHighlight',
  'hairBackOutline',
]

const ORDINARY_ROLE_ORDER: readonly LayerRole[] = [
  'skin',
  'shadow',
  'highlight',
  'outline',
]

const EYE_ROLE_ORDER: readonly LayerRole[] = [
  'whites',
  'pupilColour',
  'pupilDetail',
  'pupilReflection',
  'skin',
  'outline',
  'shadow',
  'highlight',
]

export interface StackedLayer {
  role: LayerRole
  url: string
  blend: CssBlend
}

const PREFERRED_FORMATS = ['png', 'webp', 'avif']

function pickUrl(formats: Record<string, string>): string | undefined {
  for (const format of PREFERRED_FORMATS) {
    if (formats[format]) {
      return formats[format]
    }
  }

  return Object.values(formats)[0]
}

export function stackLayers(layers: LayerUrls): StackedLayer[] {
  const present = Object.keys(layers) as LayerRole[]
  const front = present.includes('whites') ? EYE_ROLE_ORDER : ORDINARY_ROLE_ORDER
  const order = [...BACK_ROLE_ORDER, ...front]

  return order.reduce<StackedLayer[]>((stack, role) => {
    const formats = layers[role]
    if (!formats) {
      return stack
    }
    const url = pickUrl(formats)
    if (!url) {
      return stack
    }

    return [...stack, { role, url, blend: ROLE_BLEND[role] }]
  }, [])
}

export const MAX_TIER = 9

export const ART_TIER_COUNT = 6

export function unlocksAtTier(
  rule: TierRule | null,
  index: number,
  slug: string,
): number | null {
  if (!rule) {
    return null
  }
  if (rule.kind === 'slugs') {
    const at = rule.perSlug[slug]
    // A slug the rule does not mention is UNGATED, matching `tier-gate.ts` in the API. `null` is
    // reserved for the `count` case where no tier reaches an option at all.
    return at === undefined ? 0 : clampTier(at)
  }

  for (let tier = 0; tier <= MAX_TIER; tier += 1) {
    if (index < countAt(rule.perTier, tier)) {
      return tier
    }
  }

  return null
}

function countAt(perTier: PerTierEntry[], tier: number): number {
  const value = perTier[tier]
  if (value === 'all') {
    return Number.MAX_SAFE_INTEGER
  }

  return typeof value === 'number' ? value : 0
}

const clampTier = (n: number): number =>
  Math.max(0, Math.min(MAX_TIER, Math.trunc(n)))

export interface SortableOption {
  category: string
  sortOrder: number
  slug: string
}

export function sortedOptionsFor(
  options: SortableOption[],
  sectionId: string,
) {
  return options
    .filter((o) => o.category === sectionId)
    .sort((a, b) =>
      a.sortOrder === b.sortOrder
        ? a.slug.localeCompare(b.slug)
        : a.sortOrder - b.sortOrder,
    )
}
